import { Modal } from '@iwoca/orion';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { BankSelectionBody } from './BankSelectionBody';
import { LinkedAccount } from './getLinkedAccounts';
import { ModalStep, useBankSelectionFlow } from './useBankSelectionFlow';
import { getCountry } from '../../../iwIntlProvider/CountryProvider';
import { clearTrueLayerParams } from '../../../iwRouter/LocationHelper';

export const BankSelectionModal = ({
    isOpen,
    onClose,
    initialStep = 'choose',
    linkedAccounts = []
}: {
    isOpen: boolean;
    onClose: () => void;
    initialStep?: ModalStep;
    linkedAccounts?: LinkedAccount[];
}) => {
    const flow = useBankSelectionFlow(initialStep);
    const hasLinkedAccount = linkedAccounts.length > 0;
    const { title, isDefaultTitle } = getTitle(flow.step, hasLinkedAccount);
    const subTitle = getSubTitle(flow.step);

    const handleDone = () => {
        clearTrueLayerParams();
        onClose();
    };
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            closeIcon={true}
            headerBar={true}
            title={title}
            subTitle={subTitle}
            data-testid="bank-selection-modal"
            className={classNames('fs-mask dd-privacy-mask', {
                '[&_[data-testid=modal-title]]:text-center': isDefaultTitle
            })}
        >
            <BankSelectionBody
                step={flow.step}
                selectedBank={flow.selectedBank}
                selectBank={flow.selectBank}
                unselectBank={flow.unselectBank}
                onLinkAnother={flow.linkAnother}
                linkedAccounts={linkedAccounts}
                onDone={handleDone}
            />
        </Modal>
    );
};

function getTitle(currentStep: ModalStep, hasLinkedAccount: boolean) {
    if (currentStep === 'success') {
        return {
            isDefaultTitle: false,
            title: (
                <FormattedMessage
                    id="_.bank_selection_modal.success_title"
                    defaultMessage="You have successfully linked your business bank account"
                />
            )
        };
    }

    if (currentStep === 'choose' && hasLinkedAccount && getCountry() === 'UK') {
        return {
            isDefaultTitle: false,
            title: (
                <FormattedMessage
                    id="_.bank_selection_modal.link_additional_title"
                    defaultMessage="You’re linking an additional account"
                />
            )
        };
    }

    return {
        isDefaultTitle: true,
        title: (
            <FormattedMessage
                id="_.bank_selection_modal.title"
                defaultMessage="Connect your business account"
            />
        )
    };
}

function getSubTitle(currentStep: ModalStep) {
    if (currentStep === 'success') {
        return (
            <FormattedMessage
                id="_.bank_selection_modal.success_subtitle"
                defaultMessage="You may now close this tab or link an additional account below."
            />
        );
    }

    return undefined;
}
